import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { AlmasaLoading } from "@/components/brand/AlmasaLoading";
import { NotificacionesSistema } from "@/components/NotificacionesSistema";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import { UsuariosConectadosPanel } from "@/components/admin/UsuariosConectadosPanel";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSystemPresence } from "@/hooks/useSystemPresence";
import { useIsMobile } from "@/hooks/use-mobile";
import { KPICards } from "@/components/dashboard/KPICards";
import { EstadoOperacionesPanel } from "@/components/dashboard/EstadoOperacionesPanel";
import { EstadoOperacionesMobile } from "@/components/dashboard/EstadoOperacionesMobile";
import { MapaRutasWidget } from "@/components/dashboard/MapaRutasWidget";
import { VentasMensualesChart } from "@/components/dashboard/VentasMensualesChart";
import { CobranzaCriticaPanel } from "@/components/dashboard/CobranzaCriticaPanel";
import { CreditoExcedidoAlert } from "@/components/dashboard/CreditoExcedidoAlert";
import { VendedoresResumen } from "@/components/dashboard/VendedoresResumen";
import { EntregasHoyPanel } from "@/components/dashboard/EntregasHoyPanel";
import { InventarioResumen } from "@/components/dashboard/InventarioResumen";
import { AlertasUrgentes } from "@/components/dashboard/AlertasUrgentes";
import { TopProductosClientesPanel } from "@/components/dashboard/TopProductosClientesPanel";
import { ResumenFinancieroPanel } from "@/components/dashboard/ResumenFinancieroPanel";
import { useDashboardData, type Periodo } from "@/components/dashboard/useDashboardData";
import { EntregasEnDescargaWidget } from "@/components/dashboard/EntregasEnDescargaWidget";
import { ResumenDiaWidget } from "@/components/dashboard/ResumenDiaWidget";
import { COMPANY_DATA } from "@/constants/companyData";
import { EmpleadosPruebaAlert } from "@/components/dashboard/EmpleadosPruebaAlert";
import { CumpleanosWidget } from "@/components/dashboard/CumpleanosWidget";
import { LicenciasVencimientoAlert } from "@/components/dashboard/LicenciasVencimientoAlert";
import { VacacionesHoyWidget } from "@/components/dashboard/VacacionesHoyWidget";
import { AsistenciaHoyWidget } from "@/components/dashboard/AsistenciaHoyWidget";
import { VentasBajoCostoWidget } from "@/components/dashboard/VentasBajoCostoWidget";
import { PageHeader } from "@/components/layout/PageHeader";
import { supabase } from "@/integrations/supabase/client";

// Editorial section helper
function DashSection({ title, defaultOpen = true, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="flex items-center justify-between w-full py-2 group cursor-pointer">
          <div className="text-[9px] uppercase tracking-[0.22em] text-ink-400 font-medium">— {title}</div>
          {open ? <ChevronUp className="h-3.5 w-3.5 text-ink-300" /> : <ChevronDown className="h-3.5 w-3.5 text-ink-300" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 mt-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { roles, isLoading: rolesLoading, isAdmin } = useUserRoles();
  const isMobile = useIsMobile();
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [dashTab, setDashTab] = useState("general");
  const { data: dashData, loading: dashLoading, refresh, lastRefresh } = useDashboardData(periodo);
  const [userName, setUserName] = useState<string>('');

  useSystemPresence('dashboard');

  // Get user's first name
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: emp } = await supabase
          .from('empleados')
          .select('nombre, nombre_completo')
          .eq('user_id', user.id)
          .maybeSingle();
        if (emp) {
          setUserName((emp.nombre || emp.nombre_completo?.split(' ')[0] || '').trim());
        }
      }
    };
    getUser();
  }, []);

  // Redirect almacen/chofer
  useEffect(() => {
    if (!rolesLoading && roles.length > 0) {
      if (roles.length === 1 && roles.includes('almacen')) {
        navigate('/almacen-tablet', { replace: true });
        return;
      }
      if (roles.length === 1 && roles.includes('chofer')) {
        navigate('/chofer', { replace: true });
        return;
      }
    }
  }, [roles, rolesLoading, navigate]);

  if (rolesLoading) {
    return (
      <Layout>
        <div className="space-y-4 md:space-y-6">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-4 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-16" />
              </Card>
            ))}
          </div>
          <Skeleton className="h-[300px] w-full rounded-lg" />
        </div>
      </Layout>
    );
  }

  const isOnlyAlmacen = roles.length === 1 && roles.includes('almacen');
  const isOnlyChofer = roles.length === 1 && roles.includes('chofer');
  if (isOnlyAlmacen || isOnlyChofer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <AlmasaLoading size={48} text="Cargando..." />
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          eyebrow="Hoy"
          title="Buenos días,"
          titleAccent={`${userName || 'Jose'}.`}
          lead="Aquí está cómo va ALMASA hoy."
          actions={
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5 p-1 bg-bg-soft border border-ink-100 rounded-lg">
                {(['hoy', 'semana', 'mes', 'anio'] as Periodo[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriodo(p)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      periodo === p
                        ? 'text-white bg-crimson-500 shadow-xs-soft'
                        : 'text-ink-700 hover:bg-white hover:shadow-xs-soft'
                    }`}
                  >
                    {p === 'hoy' ? 'Hoy' : p === 'semana' ? 'Semana' : p === 'mes' ? 'Mes' : 'Año'}
                  </button>
                ))}
              </div>
              <button
                onClick={() => refresh()}
                title="Actualizar"
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-ink-200 bg-white text-ink-700 hover:bg-bg-soft transition-all"
              >
                <RefreshCw className={`h-4 w-4 ${dashLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          }
        />

        <NotificacionesSistema />

        {/* ALERTAS URGENTES — Always on top */}
        {dashData && <AlertasUrgentes alertas={dashData.alertas} />}

        {/* Entregas en descarga */}
        {(dashData?.kpis?.entregasEnDescarga > 0 || (dashData?.kpis?.entregasCompletadasHoyDetalle?.length || 0) > 0) && (
          <EntregasEnDescargaWidget
            entregas={dashData?.kpis?.entregasEnDescargaDetalle || []}
            completadasHoy={dashData?.kpis?.entregasCompletadasHoyDetalle || []}
          />
        )}

        {/* Admin tabs: General | RRHH | Finanzas */}
        {isAdmin ? (
          <Tabs value={dashTab} onValueChange={setDashTab}>
            <TabsList className="bg-transparent border-b border-ink-100 rounded-none p-0 h-auto gap-6 mb-8">
              <TabsTrigger value="general" className="px-0 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-crimson-500 data-[state=active]:border-b-2 data-[state=active]:border-crimson-500 rounded-none text-ink-500 font-medium text-sm">General</TabsTrigger>
              <TabsTrigger value="rrhh" className="px-0 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-crimson-500 data-[state=active]:border-b-2 data-[state=active]:border-crimson-500 rounded-none text-ink-500 font-medium text-sm">RRHH</TabsTrigger>
              <TabsTrigger value="finanzas" className="px-0 py-3 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-crimson-500 data-[state=active]:border-b-2 data-[state=active]:border-crimson-500 rounded-none text-ink-500 font-medium text-sm">Finanzas</TabsTrigger>
            </TabsList>

            {/* ==================== TAB: GENERAL ==================== */}
            <TabsContent value="general" className="space-y-10 mt-0">
              {/* KPIs */}
              <KPICards data={dashData?.kpis ?? null} loading={dashLoading} />

              {/* Operations */}
              <DashSection title="Operaciones">
                {isMobile ? <EstadoOperacionesMobile /> : <EstadoOperacionesPanel />}
                <MapaRutasWidget />
                {!isMobile && <UsuariosConectadosPanel />}
              </DashSection>

              {/* Quick metrics */}
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                <AsistenciaHoyWidget />
                <div className="cursor-pointer" onClick={() => navigate('/clientes')}><CreditoExcedidoAlert /></div>
                <VendedoresResumen />
                <div className="cursor-pointer" onClick={() => navigate('/rutas')}><EntregasHoyPanel /></div>
                <div className="cursor-pointer" onClick={() => navigate('/inventario')}><InventarioResumen /></div>
              </div>

              {/* Ventas bajo costo */}
              <VentasBajoCostoWidget />
            </TabsContent>

            {/* ==================== TAB: RRHH ==================== */}
            <TabsContent value="rrhh" className="space-y-4 mt-0">
              <EmpleadosPruebaAlert />
              <CumpleanosWidget />
              <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                <VacacionesHoyWidget />
                <LicenciasVencimientoAlert />
              </div>
              <AsistenciaHoyWidget />
            </TabsContent>

            {/* ==================== TAB: FINANZAS ==================== */}
            <TabsContent value="finanzas" className="space-y-4 mt-0">
              <ResumenDiaWidget />
              <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                <VentasMensualesChart />
                <CobranzaCriticaPanel />
              </div>
              {dashData && (
                <TopProductosClientesPanel
                  topProductos={dashData.topProductos}
                  topClientes={dashData.topClientes}
                />
              )}
              {dashData && <ResumenFinancieroPanel data={dashData.resumenFinanciero} />}
            </TabsContent>
          </Tabs>
        ) : (
          /* Non-admin: simple layout without tabs */
          <div className="space-y-4">
            <KPICards data={dashData?.kpis ?? null} loading={dashLoading} />
            {isMobile ? <EstadoOperacionesMobile /> : <EstadoOperacionesPanel />}
            <MapaRutasWidget />
            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
              <VentasMensualesChart />
              <CobranzaCriticaPanel />
            </div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
              <div className="cursor-pointer" onClick={() => navigate('/clientes')}><CreditoExcedidoAlert /></div>
              <VendedoresResumen />
              <div className="cursor-pointer" onClick={() => navigate('/rutas')}><EntregasHoyPanel /></div>
              <div className="cursor-pointer" onClick={() => navigate('/inventario')}><InventarioResumen /></div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
